/**
 * Camada de acesso HTTP à API FastAPI. Nenhuma lógica de negócio aqui.
 *
 * Espelha o `client/api.py` do painel antigo:
 * - Todas as chamadas passam pelo servidor Bun em `/api/*`, que injeta o
 *   `Authorization: Bearer <JWT>` e renova o token quando a API responde 401.
 * - Resposta fora de 2xx vira `ApiHttpError` (o `raise_for_status` do httpx),
 *   com a mesma mensagem que o httpx gera.
 * - Tempo esgotado vira `ApiTimeoutError`; falha de rede vira `ApiConnectionError`.
 *
 * As funções por domínio ficam em `src/api/<dominio>.ts` e usam `apiFetch`.
 */

/** Timeout padrão das chamadas (httpx.Timeout(45.0)). */
export const TIMEOUT_PADRAO_MS = 45_000
/** Relatórios e issues estagnadas (httpx.Timeout(120.0)). */
export const TIMEOUT_RELATORIOS_MS = 120_000
/** Preview/execução de merge por épico ou milestone (leitura de até 600 s). */
export const TIMEOUT_EPIC_ENV_MR_MS = 600_000
/** Verificação de saúde da API (httpx.Timeout(5.0)). */
export const TIMEOUT_HEALTH_MS = 5_000

const PREFIXO_API = '/api'

/** Valor aceito em query string. `undefined` é omitido; `null` vira vazio (como no httpx). */
export type ValorParametro = string | number | boolean | null | undefined
export type Parametros = Record<string, ValorParametro | readonly ValorParametro[]>

export interface OpcoesRequisicao {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Query string. Listas repetem a chave (`a=1&a=2`), como no httpx. */
  params?: Parametros
  /** Corpo JSON. */
  json?: unknown
  /** Corpo bruto (ex.: FormData para upload). */
  body?: BodyInit
  /** Timeout em milissegundos (padrão: 45 s). */
  timeoutMs?: number
  signal?: AbortSignal
  headers?: Record<string, string>
  /**
   * Modo de cache do navegador (`fetch` `cache`). Use `'no-store'` quando
   * chamadas ao mesmo endereço podem acontecer ao mesmo tempo: sem isso o
   * navegador segura uma até a outra terminar.
   */
  cache?: RequestCache
}

/** Resposta HTTP fora de 2xx (equivalente ao `httpx.HTTPStatusError`). */
export class ApiHttpError extends Error {
  readonly status: number
  readonly statusText: string
  /** URL chamada no backend (com a query string). */
  readonly url: string
  /** Corpo da resposta já interpretado (JSON quando possível, senão texto). */
  readonly corpo: unknown
  /** Texto bruto da resposta. */
  readonly texto: string

  constructor(status: number, statusText: string, url: string, corpo: unknown, texto: string) {
    super(mensagemHttpx(status, statusText, url))
    this.name = 'ApiHttpError'
    this.status = status
    this.statusText = statusText
    this.url = url
    this.corpo = corpo
    this.texto = texto
  }

  /** Campo `detail` do corpo (FastAPI), quando existir. */
  get detail(): unknown {
    if (this.corpo && typeof this.corpo === 'object' && 'detail' in this.corpo) {
      return this.corpo.detail
    }
    return undefined
  }
}

/** Tempo esgotado (equivalente ao `httpx.TimeoutException`). */
export class ApiTimeoutError extends Error {
  constructor() {
    super('timed out')
    this.name = 'ApiTimeoutError'
  }
}

/** Falha de conexão com o servidor (equivalente ao `httpx.RequestError`). */
export class ApiConnectionError extends Error {
  constructor(mensagem: string) {
    super(mensagem)
    this.name = 'ApiConnectionError'
  }
}

const FRASES_STATUS: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  409: 'Conflict',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
}

/** Mesma mensagem do `raise_for_status` do httpx. */
function mensagemHttpx(status: number, statusText: string, url: string): string {
  const tipo =
    status < 200
      ? 'Informational response'
      : status < 400
        ? 'Redirect response'
        : status < 500
          ? 'Client error'
          : 'Server error'
  const frase = statusText || FRASES_STATUS[status] || ''
  return (
    `${tipo} '${status} ${frase}' for url '${url}'\n` +
    `For more information check: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/${status}`
  )
}

function valorTexto(valor: ValorParametro): string | null {
  if (valor === undefined) return null
  if (valor === null) return ''
  if (typeof valor === 'boolean') return valor ? 'true' : 'false'
  return String(valor)
}

/** Monta a query string no mesmo formato do httpx. */
export function montarQuery(params?: Parametros): string {
  if (!params) return ''
  const busca = new URLSearchParams()
  for (const [chave, bruto] of Object.entries(params)) {
    const valores: readonly ValorParametro[] = Array.isArray(bruto)
      ? (bruto as readonly ValorParametro[])
      : [bruto as ValorParametro]
    for (const valor of valores) {
      const texto = valorTexto(valor)
      if (texto !== null) busca.append(chave, texto)
    }
  }
  const qs = busca.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Faz a requisição e devolve a `Response` sem checar o status.
 * Use `apiFetch` no dia a dia; esta serve para casos como o health check.
 */
export async function apiRequest(
  caminho: string,
  opcoes: OpcoesRequisicao = {}
): Promise<Response> {
  const { method = 'GET', params, json, body, timeoutMs = TIMEOUT_PADRAO_MS, signal } = opcoes
  const headers: Record<string, string> = { Accept: 'application/json', ...opcoes.headers }
  let corpo: BodyInit | undefined = body
  if (json !== undefined) {
    headers['Content-Type'] = 'application/json'
    corpo = JSON.stringify(json)
  }
  const sinalTimeout = AbortSignal.timeout(timeoutMs)
  const sinal = signal ? AbortSignal.any([signal, sinalTimeout]) : sinalTimeout
  try {
    return await fetch(`${PREFIXO_API}${caminho}${montarQuery(params)}`, {
      method,
      headers,
      body: corpo,
      signal: sinal,
      credentials: 'same-origin',
      ...(opcoes.cache ? { cache: opcoes.cache } : {}),
    })
  } catch (erro) {
    if (sinalTimeout.aborted) throw new ApiTimeoutError()
    if (signal?.aborted) throw erro
    throw new ApiConnectionError(erro instanceof Error ? erro.message : String(erro))
  }
}

async function lerCorpo(resposta: Response): Promise<{ corpo: unknown; texto: string }> {
  const texto = await resposta.text()
  if (!texto) return { corpo: null, texto }
  try {
    return { corpo: JSON.parse(texto) as unknown, texto }
  } catch {
    return { corpo: texto, texto }
  }
}

/**
 * Chama a API e devolve o JSON da resposta. Lança `ApiHttpError` para status
 * fora de 2xx (como `r.raise_for_status()`), `ApiTimeoutError` ou `ApiConnectionError`.
 */
export async function apiFetch<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const resposta = await apiRequest(caminho, opcoes)
  const { corpo, texto } = await lerCorpo(resposta)
  if (!resposta.ok) {
    const urlBackend =
      resposta.headers.get('X-Backend-Url') ??
      `${PREFIXO_API}${caminho}${montarQuery(opcoes.params)}`
    const frase = resposta.headers.get('X-Backend-Status-Text') ?? resposta.statusText
    throw new ApiHttpError(resposta.status, frase, urlBackend, corpo, texto)
  }
  return corpo as T
}

/**
 * Igual ao `apiFetch`, mas devolve o texto da resposta sem ler o JSON (para
 * quem precisa interpretar o corpo do seu jeito).
 */
export async function apiFetchTexto(
  caminho: string,
  opcoes: OpcoesRequisicao = {}
): Promise<string> {
  const resposta = await apiRequest(caminho, opcoes)
  const { corpo, texto } = await lerCorpo(resposta)
  if (!resposta.ok) {
    const urlBackend =
      resposta.headers.get('X-Backend-Url') ??
      `${PREFIXO_API}${caminho}${montarQuery(opcoes.params)}`
    const frase = resposta.headers.get('X-Backend-Status-Text') ?? resposta.statusText
    throw new ApiHttpError(resposta.status, frase, urlBackend, corpo, texto)
  }
  return texto
}

/**
 * Texto de erro para exibir ao usuário: o `detail` da API quando existir,
 * senão a mensagem da exceção (mesma regra do `_extrair_detail` das páginas).
 */
export function extrairDetail(erro: unknown): string {
  if (erro instanceof ApiHttpError) {
    const detail = erro.detail
    if (detail) return typeof detail === 'string' ? detail : JSON.stringify(detail)
  }
  return erro instanceof Error ? erro.message : String(erro)
}

/** `GET /health` com timeout de 5 s: true só com HTTP 200 (igual ao `api.health_check`). */
export async function healthCheck(): Promise<boolean> {
  try {
    const resposta = await apiRequest('/health', { timeoutMs: TIMEOUT_HEALTH_MS })
    return resposta.status === 200
  } catch {
    return false
  }
}
